"use client";

import { useMemo } from "react";

import { BuilderContent } from "@builder.io/sdk-react";
import Image from "next/image";
import { useRouter } from "next/navigation";

import { Header as UIHeader } from "@jti/ui/eva-components/Header";
import { useConfig } from "@jti/ui/providers/useConfig";

import { useLocale } from "@/context/LocaleContext";
import { usePageConfig } from "@/context/PageConfigContext";
import { useLanguages } from "@/hooks/useLanguages";
import { usePathnameWithoutLocale } from "@/hooks/usePathnameWithoutLocale";
import { Content } from "@/libs/builder/Content";
import {
  isReferenceObjectWithValue,
  ReferenceObject,
} from "@/libs/builder/tools/isReferenceObjectWithValue";
import { getPathnameWithoutLocale } from "@/utils/getPathnameWithoutLocale";

import { CartMini } from "../CartMini/CartMini";

import { HeaderProps } from "./inputs/headerInputs.builder";

import styles from "./Header.module.scss";

export function Header(props: HeaderProps) {
  const {
    logo,
    navLinks = [],
    tools: { accountLink, mobileAppLink, storeLocatorLink, resultsLink },
    products,
    accountDropdownSettings,
  } = props;

  const {
    darkMode,
    hideNavigation,
    simplifiedHeader,
    hideBreadcrumbs,
    breadcrumbsList,
    overlayHeader,
    useHeadingTagForBreadcrumbs,
    navigationThemeOverride,
  } = usePageConfig();

  const { DISABLE_COMMERCE } = useConfig();
  const languages = useLanguages();
  const router = useRouter();
  const { currentLocale } = useLocale();
  const pathnameWithoutLocale = usePathnameWithoutLocale();
  const breadcrumbs = hideBreadcrumbs ? [] : breadcrumbsList;

  const renderLogoImage = ({ src, alt }: { src: string; alt: string }) => (
    <Image src={src} alt={alt} layout="fill" priority className={styles.logo} />
  );

  const validatedNavLinks = useMemo(
    () =>
      navLinks.map(({ secondaryNavigation, ...rest }) => {
        if (
          secondaryNavigation &&
          isReferenceObjectWithValue<BuilderContent>(secondaryNavigation)
        ) {
          return {
            ...rest,
            secondaryNavigation,
            isActive: pathnameWithoutLocale === rest.url,
          };
        } else {
          return {
            ...rest,
            isActive: pathnameWithoutLocale === rest.url,
          };
        }
      }),
    [navLinks, pathnameWithoutLocale]
  );

  const secondaryNavigationList = useMemo(() => {
    return validatedNavLinks.reduce<ReferenceObject<BuilderContent>[]>(
      (acc, navLink) => {
        if ("secondaryNavigation" in navLink) {
          acc.push(navLink.secondaryNavigation);
        }
        return acc;
      },
      []
    );
  }, [validatedNavLinks]);

  function onLogout() {
    const logoutRedirectPage = accountDropdownSettings?.logoutRedirectPage;
    if (
      logoutRedirectPage &&
      pathnameWithoutLocale !=
        getPathnameWithoutLocale(logoutRedirectPage, currentLocale)
    ) {
      router.push(logoutRedirectPage);
    } else {
      window.location.reload();
    }
  }

  return (
    <UIHeader
      isDarkMode={navigationThemeOverride || darkMode}
      logo={{
        ...logo,
        renderImage: renderLogoImage,
      }}
      hideNavigation={hideNavigation}
      isSimplified={simplifiedHeader}
      navLinks={validatedNavLinks}
      accountLink={accountLink}
      accountDropdownSettings={accountDropdownSettings}
      renderMiniCart={() => <CartMini />}
      onLogOut={onLogout}
      mobileAppLink={mobileAppLink}
      storeLocatorLink={storeLocatorLink}
      breadcrumbsList={breadcrumbs}
      languages={languages}
      useHeadingTagForBreadcrumbs={useHeadingTagForBreadcrumbs}
      resultsLink={resultsLink}
      withCart={!DISABLE_COMMERCE}
      overlayHeader={overlayHeader}
      renderSubmenu={(secondaryNavigationId) => {
        const content = secondaryNavigationList.find(
          ({ id }) => secondaryNavigationId === id
        )?.value;

        return (
          <Content
            model="secondary-navigation"
            content={content}
            data={{
              products,
            }}
          />
        );
      }}
    />
  );
}
